import { v4 as uuidv4 } from 'uuid';
import { buildCloudFrontUrl } from '@/utils/pathUtils';
import { uploadImageToS3 } from '@/utils/imageUtils';
import { getCanvasElementTitle } from '@/utils/elementsUtils';

(function () {
    'ngInject';

    angular.module('app.shared.services').factory('DynamicImageService', dynamicImageService);

    function dynamicImageService(
        $rootScope,
        blockUI,
        $q,
        $mdDialog,
        ImageAPIService,
        TestImageService,
        LibrariesAPIService,
        MultipleFontService,
        ElementsColorService
    ) {
        function initializeDynamicImagesMap(imageProperties) {
            // (json -> Map) As dynamic images are stored as a json on the database, and the app expect them to be a map, we'll transform them.
            let dynamicImagesMap = new Map();
            if (imageProperties.dynamicImagesPath && !(imageProperties.dynamicImagesPath instanceof Map)) {
                JSON.parse(imageProperties.dynamicImagesPath).map(function (element) {
                    dynamicImagesMap.set(element[0], element[1]);
                });
                imageProperties.dynamicImagesPath = dynamicImagesMap;
            }
        }

        async function saveDynamicImageToPlaceholder(
            position,
            file,
            canvas,
            fileName = null,
            path = 'preview',
            object
        ) {
            let canvasObject = object;
            if (canvasObject.type == 'image-placeholder' && canvasObject.dynamicImagesPath) {
                blockUI.start('Saving Images');
                return uploadImageToS3(path, file).then((response) => {
                    $rootScope.$apply(() => {
                        fileName
                            ? canvasObject.dynamicImagesPath.set(position, {
                                  path: response.Path,
                                  fileName: fileName.split('.')[0]
                              })
                            : canvasObject.dynamicImagesPath.set(position, response.Path);
                        blockUI.stop();
                    });
                });
            }
        }

        function showDynamicImage(canvas, object, position) {
            return $q(async function (resolve, reject) {
                if (typeof position === 'undefined') {
                    resolve();
                    return;
                }

                let imageUrl = '';
                object.dynamicImageVariation = position;
                object.dynamicImagesPath = parseDynamicImagesPath(object.dynamicImagesPath);
                const selectedImage = object.dynamicImagesPath.get(position);
                // If there's a images library, used it instead
                if (object.imageLibraryId) {
                    const imageObject = await LibrariesAPIService.getLibraryElementAtPosition(
                        object.imageLibraryId,
                        position
                    );

                    if (imageObject) {
                        imageUrl = imageObject.Path;
                    }
                } else if (object.dynamicImagesPath && selectedImage) {
                    //verifying if the Map value have is an object or just the path
                    imageUrl = selectedImage.path ? selectedImage.path : selectedImage;
                }

                if (imageUrl) {
                    try {
                        const options = {
                            crossOrigin: 'Anonymous'
                        };
                        fabric.util.loadImage(
                            buildCloudFrontUrl(imageUrl),
                            function (img, error) {
                                if (error) {
                                    reject();
                                }
                                object.setElement(img);
                                canvas.requestRenderAll();
                                resolve();
                            },
                            null,
                            options && options.crossOrigin
                        );
                    } catch (error) {
                        console.error('An error occurred while loading the image', error);
                        reject();
                    }
                } else {
                    showSampleDynamicImage(canvas, object);
                    resolve();
                }
            });
        }

        function showSampleDynamicImage(canvas, object) {
            // If there is no image saved in that position show the base image (just if its not a printarea or a productColors image, if so show an empty one)
            //  https://stackoverflow.com/a/9967193/1519464
            const { isColorOptions, isPrintArea, imageLibraryId, dynamicImagesPath } = object;
            const imageUrl =
                !isColorOptions && !isPrintArea && !imageLibraryId && dynamicImagesPath.size === 0
                    ? require('@/assets/images/dynamic_image.png')
                    : 'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==';

            TestImageService.showStaticImage(canvas, object, { imageUrl });
        }

        function getLibraryPositionsFromDynamicImages(elements) {
            const filteredElements = elements.filter((item) => item.object?.imageLibraryId);
            const libraryIdsSet = new Set(filteredElements.map((item) => item.object?.imageLibraryId));
            const libraryIds = Array.from(libraryIdsSet);
            const position = filteredElements.length > 0 ? filteredElements[0].position : null;

            return {
                libraryIds: libraryIds,
                position: position
            };
        }

        function buildDynamicImageProperties(canvas, position, override = {}) {
            const canvasWidth = canvas ? (canvas.getArtworkWidth() / canvas.getZoom()) * canvas.getEffectiveZoom() : 0;
            const canvasHeight = canvas ? (canvas.getArtworkHeight() / canvas.getZoom()) * canvas.getEffectiveZoom() : 0;
            return {
                originX: 'center',
                originY: 'center',
                centeredRotation: true,
                left: canvasWidth / 2,
                top: canvasHeight / 2,
                stroke: 'black',
                strokeWidth: canvas ? canvas.getVisibleStrokeWidth() : 0,
                strokeDashArray: canvas ? canvas.getDashedArray() : 0,
                id: position,
                uuid: uuidv4(),
                fullColor: true,
                grading: false,
                lockSkewingX: true,
                lockSkewingY: true,
                lockScalingFlip: true,
                width: 0.3 * canvasWidth,
                height: 0.3 * canvasHeight,
                zIndex: canvas ? canvas.getHighestZIndex() + 1 : 1,
                imageUrl: false,
                hasStaticImage: false,
                dynamicImagesPath: new Map(),
                dynamicImageVariation: 1,
                color: MultipleFontService.createColorObj({
                    hex: '#000000',
                    cmyk: '0,0,0,1'
                }),
                preventEditMode: true,
                isOverlay: false,
                isPrintArea: false,
                isColorOptions: false,
                podProductImagesMetadata: null,
                pageEpsId: canvas ? canvas.getFirstActivePageEpsId() : 1,
                textMaskUuid: '',
                ...override
            };
        }

        function createDynamicImage(canvas, position, override = {}) {
            const dynamicImageProperties = buildDynamicImageProperties(canvas, position, override);
            const image = new fabric.ImagePlaceholder(null, dynamicImageProperties);
            image.name = getCanvasElementTitle(image);

            return image;
        }

        /**
         * Converts the dynamicImagesPath property from dynamic images to a Map
         * @param {String|Map} dynamicImagesPath dynamicImagesPath property from dynamic images
         * @returns {Map<number, string>}
         */
        function parseDynamicImagesPath(dynamicImagesPath) {
            // Create a Map from the serialized string
            if (typeof dynamicImagesPath === 'string') {
                const dynamicImagesMap = new Map();
                JSON.parse(dynamicImagesPath).map(function (element) {
                    dynamicImagesMap.set(element[0], element[1]);
                });

                return dynamicImagesMap;
            }

            // Already a map
            return dynamicImagesPath;
        }

        function limitDynamicImageFileSizes(prodDynamicImages, previewDynamicImages, maxImageSize) {
            return new Promise(async (resolve, reject) => {
                try {
                    const imagesArrays = prodDynamicImages.map((i) =>
                        Array.from(parseDynamicImagesPath(i.dynamicImagesPath).values())
                    );
                    const imagePaths = [].concat.apply([], imagesArrays);
                    const filenames = imagePaths.map((image) =>
                        image.path ? image.path.replace(/^.*[\\\/]/, '') : image.replace(/^.*[\\\/]/, '')
                    );
                    const blockUiMessage = blockUI.message();
                    const isBlockUiActive = blockUI.state().blockCount > 0;

                    // Check if images need resizing
                    blockUI.start('Checking images');
                    const needResizing = await ImageAPIService.doDynamicImagesNeedResizing(filenames, maxImageSize);
                    blockUI.stop();

                    // We need to resize the images, alert the user
                    if (needResizing) {
                        // Stop blockUI to be able to show the modal
                        blockUI.stop();
                        const confirm = $mdDialog
                            .confirm()
                            .title('Large Dynamic Images')
                            .textContent(
                                'There are large images loaded into Dynamic Image elements that will result in bad performance and slow loading times on your store, do you want to resize them to an optimal size?'
                            )
                            .ok('Yes')
                            .cancel('No');

                        try {
                            // Ask user for images resize
                            await $mdDialog.show(confirm);

                            try {
                                // Resize dynamic images
                                blockUI.start('Resizing images!');
                                const response = await ImageAPIService.resizeDynamicImages(filenames, maxImageSize);

                                // Apply the new images to the elements
                                for (const previewImage of previewDynamicImages) {
                                    // Go through each preview image and put the new resized image path
                                    const images = parseDynamicImagesPath(previewImage.dynamicImagesPath);
                                    for (const image of images) {
                                        // Find the image in the renamed images
                                        const renamedImage = response.find(
                                            (i) => i.OldFilename === image[1].replace(/^.*[\\\/]/, '')
                                        );
                                        if (renamedImage) {
                                            // Replace the filename with the new one
                                            const newPath = image[1].replace(
                                                /(.*)\/.*/i,
                                                `$1/${renamedImage.NewFilename}`
                                            );
                                            images.set(image[0], newPath);
                                        }
                                    }

                                    // Set the new images for the dynamic image
                                    previewImage.dynamicImagesPath = JSON.stringify(Array.from(images.entries()));
                                }
                                blockUI.stop();
                            } catch (error) {
                                // Don't resize images
                                blockUI.stop();
                                console.error('Error resizing dynamic images', error);
                                reject(error);
                                return;
                            }
                        } catch (_) {
                            // Don't resize images
                        } finally {
                            // Restore blockUi
                            if (isBlockUiActive) {
                                blockUI.start(blockUiMessage);
                            }
                        }
                    }

                    resolve();
                } catch (error) {
                    console.error('Error checking for dynamic images', error);
                    reject(error);
                }
            });
        }

        async function changeDynamicImageColorPosition(canvasObject, position, colorPicker, canvas) {
            await ElementsColorService.changeDynamicColorPosition(canvasObject, position, colorPicker, canvas);
        }

        async function saveDynamicImageColor(color, canvasObject, editMode) {
            await ElementsColorService.saveDynamicColor(color, canvasObject, editMode);
        }

        return {
            parseDynamicImagesPath: parseDynamicImagesPath,
            initializeDynamicImagesMap: initializeDynamicImagesMap,
            saveDynamicImageToPlaceholder: saveDynamicImageToPlaceholder,
            createDynamicImage: createDynamicImage,
            showDynamicImage: showDynamicImage,
            getLibraryPositionsFromDynamicImages: getLibraryPositionsFromDynamicImages,
            limitDynamicImageFileSizes: limitDynamicImageFileSizes,
            changeDynamicImageColorPosition: changeDynamicImageColorPosition,
            saveDynamicImageColor: saveDynamicImageColor,
            buildDynamicImageProperties: buildDynamicImageProperties,
            showSampleDynamicImage
        };
    }
})();
